import type { TFunction } from 'i18next';

/**
 * Refusals the API raises when a POSTED invoice/bill can't be safely edited or
 * voided. Each one arrives as `cause.code` on an RFC-7807 problem body and gets
 * its own sentence, so the user is told exactly what is in the way instead of
 * "something went wrong".
 *
 * Mirrors REFUSALS in apps/api/src/modules/documents/posted-revision.service.ts.
 */
interface ReviseProblem {
  code?: string;
  cause?: {
    code?: string;
    /** period_closed → the sealed period's YYYY-MM code. */
    period?: string;
    /** already_reversed → the entry that was already reversed. */
    entryNumber?: string;
    /** has_payments → the payment doc numbers holding it. */
    payments?: string[];
    amountPaid?: string;
    /** has_linked_records → which kind of record depends on it. */
    ref?: string;
    docNumber?: string;
    /** has_linked_records → that record's id, so the screen can link/act on it. */
    refId?: string;
    /** stock_unwind_negative → what can't be given back. */
    itemName?: string | null;
    itemCode?: string | null;
    required?: string;
    available?: string;
    status?: string;
  };
}

function problemOf(err: unknown): ReviseProblem | undefined {
  return (err as { response?: { data?: ReviseProblem } }).response?.data;
}

/**
 * Translate an edit/void failure. `t` must be bound to the `documents`
 * namespace and `tc` to `common` (for the generic fallbacks) — the same pair
 * every document screen already holds.
 */
export function reviseErrorMessage(err: unknown, t: TFunction, tc: TFunction): string {
  const data = problemOf(err);
  const cause = data?.cause;
  switch (cause?.code) {
    case 'document.period_closed':
      return t('revise.err.periodClosed', {
        period: cause.period ?? '',
        defaultValue: 'That accounting period ({{period}}) is closed, so the books can no longer be changed. Re-open the period first.',
      });
    case 'document.already_reversed':
      return t('revise.err.alreadyReversed', {
        entry: cause.entryNumber ?? '',
        defaultValue: 'This document has already been reversed — journal entry {{entry}} has a reversing entry against it.',
      });
    case 'document.has_payments':
      // A credit note bumps amount_paid without creating an allocation, so the
      // payment list can legitimately be empty — name the amount instead of
      // rendering an empty "()".
      return t('revise.err.hasPayments', {
        payments: (cause.payments ?? []).length > 0
          ? (cause.payments ?? []).join(', ')
          : (cause.amountPaid ?? ''),
        defaultValue: 'A payment is allocated to this document ({{payments}}). Void or unallocate the payment first.',
      });
    case 'document.has_cashback':
      return t('revise.err.hasCashback', {
        defaultValue: 'Cashback was earned on this invoice, and it cannot be re-granted after an edit. Void the invoice and raise a new one instead.',
      });
    case 'document.has_linked_records':
      return t('revise.err.hasLinkedRecords', {
        ref: cause.docNumber ?? cause.ref ?? '',
        defaultValue: 'Another record depends on this document ({{ref}}). Remove or undo that first.',
      });
    case 'document.pos_quantity_locked':
      return t('revise.err.posQuantityLocked', {
        ref: cause.docNumber ?? '',
        defaultValue: 'This invoice came from the register ({{ref}}), which shipped the goods — so the quantities cannot be changed here. Prices, discounts and the customer can. To change what was sold, void the sale at the register and ring it again.',
      });
    case 'document.stock_unwind_negative':
      return t('revise.err.stockUnwindNegative', {
        item: cause.itemName ?? cause.itemCode ?? '',
        available: cause.available ?? '0',
        required: cause.required ?? '0',
        defaultValue: 'The stock this purchase received has already been sold or used — {{item}} needs {{required}} back but only {{available}} is on hand.',
      });
    case 'document.is_note':
      return t('revise.err.isNote', {
        defaultValue: 'Credit and debit notes are managed from their own screen.',
      });
    case 'document.not_draft':
      return t('revise.err.notDraft', {
        defaultValue: 'This document can no longer be edited.',
      });
    default:
      break;
  }
  if (data?.code === 'currency.rate_not_found') return tc('errors:currency.rate_not_found');
  if (data?.code === 'unprocessable') return tc('errors:unprocessable');
  return tc('errors:server');
}

/** A POS sale standing in the way of editing or voiding its invoice. */
export interface BlockingPosSale {
  id: string;
  saleNumber: string;
}

/**
 * The register sale blocking this revision, if that is what the refusal was.
 *
 * A POS sale owns its own undo — voiding one refunds the tender and puts the
 * stock back — which is exactly why the invoice it materialized cannot be
 * rewritten around it. Naming it is not enough though: the sentence used to
 * send the reader off to find "POS-000255" somewhere else, which on a shop with
 * hundreds of sales a day is the real cost of this refusal. Pulled out here so
 * the screen can offer the void instead of describing it.
 *
 * Returns null unless the refusal names a pos_sale AND carries its id (an older
 * API sends the number alone — then the sentence is still all we have).
 */
export function blockingPosSale(err: unknown): BlockingPosSale | null {
  const cause = problemOf(err)?.cause;
  if (cause?.code !== 'document.has_linked_records' || cause.ref !== 'pos_sale') return null;
  if (!cause.refId || !cause.docNumber) return null;
  return { id: cause.refId, saleNumber: cause.docNumber };
}
